import React, { useMemo, useEffect, useState } from 'react';
import { DonutChart } from '@/features/journal/components/charts/DonutChart';
import { get } from '@/features/shared/lib/api';

/**
 * AuthorsByCountryDonut
 * - Donut chart + legend for "Number of Authors by Country".
 * - Aligns style/behavior with AuthorsByInstitutionDonut: legend below, right‑aligned %,
 *   muted opacity for "Other" slice, and smaller fixed chart size.
 */
export default function AuthorsByCountryDonut({ items: externalItems = null, resourceId = null }) {
  // API에서 데이터 가져오기 (externalItems가 없을 때만)
  const [countryData, setCountryData] = useState(null);
  const [countryLoading, setCountryLoading] = useState(false);
  const [countryErr, setCountryErr] = useState(null);

  useEffect(() => {
    // externalItems가 제공되면 API 호출하지 않음
    if (externalItems !== null) {
      return;
    }

    // resourceId가 없으면 API 호출하지 않음 (전체 통계는 resourceId 없이 호출 가능)
    let alive = true;
    async function fetchCountryData() {
      setCountryLoading(true);
      setCountryErr(null);
      try {
        const endpoint = resourceId
          ? `/rm/v1/publication/${encodeURIComponent(resourceId)}/authors_by_country`
          : '/rm/v1/publication/authors_by_country';
        const response = await get(endpoint);
        if (alive) {
          if (response?.status && response?.result) {
            setCountryData(response.result);
          } else {
            setCountryErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setCountryData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setCountryErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setCountryLoading(false);
        }
      }
    }
    fetchCountryData();
    return () => { alive = false; };
  }, [externalItems, resourceId]);
  // Blue palette (base colors)
  const basePalette = ['#2D3BFF', '#6271FF', '#9AA5FF', '#C8CFFF', '#E4E8F1'];

  // 그라데이션 색상 생성 함수 (첫 색상부터 마지막 색상까지 순차적으로)
  const generateGradientColors = useMemo(() => {
    return (count) => {
      if (count === 0) return [];

      // RGB로 변환
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null;
      };

      // RGB를 HEX로 변환
      const rgbToHex = (r, g, b) => {
        return `#${[r, g, b].map(x => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('')}`;
      };

      // 첫 번째 색상과 마지막 색상
      const startColor = basePalette[0]; // '#2D3BFF'
      const endColor = basePalette[basePalette.length - 1]; // '#E4E8F1'

      const startRgb = hexToRgb(startColor);
      const endRgb = hexToRgb(endColor);

      if (!startRgb || !endRgb) return basePalette.slice(0, count);

      // 전체 개수만큼 첫 색상부터 마지막 색상까지 그라데이션 생성
      const colors = [];
      for (let i = 0; i < count; i++) {
        const ratio = count === 1 ? 0 : i / (count - 1);
        const r = Math.round(startRgb.r + (endRgb.r - startRgb.r) * ratio);
        const g = Math.round(startRgb.g + (endRgb.g - startRgb.g) * ratio);
        const b = Math.round(startRgb.b + (endRgb.b - startRgb.b) * ratio);
        colors.push(rgbToHex(r, g, b));
      }

      return colors;
    };
  }, [basePalette]);

  // 데이터 처리: externalItems 또는 API 데이터 사용
  const items = useMemo(() => {
    if (externalItems !== null && Array.isArray(externalItems)) {
      return externalItems;
    }
    if (countryData && Array.isArray(countryData)) {
      return countryData;
    }
    return null;
  }, [externalItems, countryData]);

  // Normalize data: attach color and percent, preserve muted
  const data = useMemo(() => {
    if (!Array.isArray(items) || !items.length) {
      return [];
    }

    const sum = items.reduce((acc, d) => acc + (Number(d.value) || 0), 0) || 1;
    const palette = generateGradientColors(items.length);

    return items.map((d, i) => ({
      label: d.label,
      value: Number(d.value) || 0,
      muted: !!d.muted,
      color: palette[i] || palette[palette.length - 1],
      percent: ((Number(d.value) || 0) / sum) * 100,
    }));
  }, [items, generateGradientColors]);

  const sum = useMemo(() => data.reduce((acc, d) => acc + (Number(d.value) || 0), 0) || 1, [data]);

  // 로딩 상태 처리
  if (externalItems === null && countryLoading) {
    return (
      <div className="flex items-center justify-center min-h-[240px] text-[#8D9AAF]">
        Loading...
      </div>
    );
  }

  // 에러 상태 처리
  if (externalItems === null && countryErr) {
    return (
      <div className="flex items-center justify-center min-h-[240px] text-[#F3652D] text-sm">
        {countryErr}
      </div>
    );
  }

  // 데이터가 없을 때 처리
  if (!data || !data.length) {
    return (
      <div className="flex items-center justify-center min-h-[240px] text-[#8D9AAF]">
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Small donut to match Institution chart */}
      <div className="w-full max-w-[160px]">
        <DonutChart title="" data={data} colors={data.map(d => d.color)} size={140} />
      </div>

      {/* Legend below */}
      <div className="w-full space-y-[6px] text-[14px] text-[#6C7687]">
        {data.map((d, idx) => (
          <div key={idx} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-block w-[10px] h-[10px] rounded-full" style={{ background: d.color, opacity: d.muted ? 0.4 : 1 }} />
              <span className="truncate" title={d.label}>{d.label}</span>
            </div>
            <span className="text-[#6C7687] shrink-0 ml-2">{(((Number(d.value) || 0) / sum) * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
